/**
 * Design tokens for the application
 * Provides standardized values for spacing, typography, and other UI elements
 * to maintain visual consistency across the application
 */

/**
 * Standardized spacing system
 * Base unit is 4px with named size variants
 * Includes mobile and desktop specific tokens
 */
export const spacing = {
  // Base unit in pixels (used for calculations)
  base: 4,
  
  // Core spacing values (can be used for margin, padding, gap)
  xs: 'px-2 py-1',     // 8px horizontal, 4px vertical
  sm: 'px-3 py-2',     // 12px horizontal, 8px vertical
  md: 'px-4 py-3',     // 16px horizontal, 12px vertical
  lg: 'px-6 py-4',     // 24px horizontal, 16px vertical
  xl: 'px-8 py-6',     // 32px horizontal, 24px vertical
  
  // Individual spacing values (for custom applications)
  px: {
    xs: 'px-2',  // 8px horizontal
    sm: 'px-3',  // 12px horizontal
    md: 'px-4',  // 16px horizontal
    lg: 'px-6',  // 24px horizontal
    xl: 'px-8',  // 32px horizontal
  },
  
  py: {
    xs: 'py-1',  // 4px vertical
    sm: 'py-2',  // 8px vertical
    md: 'py-3',  // 12px vertical
    lg: 'py-4',  // 16px vertical
    xl: 'py-6',  // 24px vertical
  },
  
  // Gap values for flex and grid layouts
  gap: {
    xs: 'gap-2',  // 8px
    sm: 'gap-3',  // 12px
    md: 'gap-4',  // 16px
    lg: 'gap-6',  // 24px
    xl: 'gap-8',  // 32px
  },
  
  // Mobile-specific adjustments
  mobile: {
    // Container level spacing (page sections, cards)
    container: 'px-4 py-4',
    
    // Section level spacing (inside containers)
    section: 'px-4 py-3',
    
    // Element level spacing (buttons, inputs)
    element: 'px-3 py-2.5',
    
    // Ensures touch targets are at least 44×44px
    touchTarget: 'min-h-[44px] min-w-[44px]',
    
    // Bottom spacing to account for mobile navigation
    withNavOffset: 'pb-16',
    
    // Form elements with optimized touch area
    formInput: 'h-12 text-base',
    formButton: 'h-12',
    
    // Margins
    margin: {
      section: 'mb-6',
      element: 'mb-4',
    },
    
    // Section spacing with dividers
    dividedSection: 'py-4 border-b border-primary-silver/30',
  },
  
  // Desktop-specific adjustments
  desktop: {
    // Container level spacing
    container: 'px-6 py-6',
    
    // Section level spacing
    section: 'px-6 py-4',
    
    // Element level spacing
    element: 'px-4 py-2',
    
    // Standard form elements
    formInput: 'h-10 text-sm',
    formButton: 'h-10',
    
    // Margins
    margin: {
      section: 'mb-8',
      element: 'mb-6',
    },
    
    // Sidebar offset
    withSidebarOffset: 'ml-[70px] max-w-[calc(100%-70px)]',
    
    // Section spacing with dividers
    dividedSection: 'py-5 border-b border-primary-silver/30',
  }
};

/**
 * Helper function to get responsive spacing based on mobile/desktop state
 * @param isMobile Boolean indicating if viewport is mobile size
 * @param variant The spacing variant to use
 * @returns The appropriate spacing class based on viewport
 */
export const getResponsiveSpacing = (
  isMobile: boolean, 
  variant: 'container' | 'section' | 'element' | 'formInput' | 'formButton' | 'dividedSection'
): string => {
  // Standard variants that exist in both mobile and desktop
  if (isMobile) {
    return spacing.mobile[variant];
  } else {
    return spacing.desktop[variant];
  }
};

// Special case utility functions for device-specific spacing
export const getNavOffset = (isMobile: boolean): string => {
  return isMobile ? spacing.mobile.withNavOffset : '';
};

export const getSidebarOffset = (isMobile: boolean): string => {
  return isMobile ? '' : spacing.desktop.withSidebarOffset;
};

export default { spacing, getResponsiveSpacing }; 